import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { DynamicCustomValidationTypes, IDynamicModel } from '../models';
import { dynamicLookupGateEnabled } from './dynamic-lookup-gate';
import moment from 'moment';

/** Invalidates control if it is loading data
 * For example a dropdown loading data when initializing
 * It is set by isLoading property in dynamic-item.base-component.ts
 */
export function dynamicValidatorNotLoading(controlLabel: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const isLoading = control && (control['isLoading_dynamic'] ?? false);
    return isLoading
      ? {
          isLoading: {
            value: `${
              controlLabel ?? 'A'
            } field on the form is still loading data ...`,
          },
        }
      : null;
  };
}

/** Invalidates control while it has an unresolved conflict that the control opted to *require*
 * resolving (conflictResolution.required === true). The flag `hasUnresolvedConflict_dynamic` is
 * set/cleared by the conflictPending setter in dynamic-item.base-component.ts. When
 * `required` is false this validator is never attached, so the form stays saveable (yellow).
 */
export function dynamicValidatorNoConflict(controlLabel: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const hasConflict =
      control && (control['hasUnresolvedConflict_dynamic'] ?? false);
    return hasConflict
      ? {
          hasUnresolvedConflict: {
            value: `${
              controlLabel ?? 'A'
            } field on the form has an unresolved value conflict to confirm.`,
          },
        }
      : null;
  };
}

/**
 * Invalidates control when its last remote typeahead search threw.
 * Flag `hasSearchError_dynamic` is set/cleared by the searchError setter on
 * dynamic-item.base-component (used by dynamic-typeahead filterChangeItem).
 * Only attached when `options.blockSubmitOnSearchFailure === true` (default off).
 */
export function dynamicValidatorNoSearchError(
  controlLabel: string
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const hasSearchError =
      control && (control['hasSearchError_dynamic'] ?? false);
    return hasSearchError
      ? {
          hasSearchError: {
            value: `${
              controlLabel ?? 'A'
            } field on the form could not load search results. Try again.`,
          },
        }
      : null;
  };
}

/**
 * Invalidates control when resolving its SAVED value (the initial prefill lookup) failed
 * (WI 35277). Unlike the opt-in search-failure block, a failed prefill leaves the control
 * showing a blank or stale value that a Save would write back, so this always blocks until a
 * retry resolves it. Flag `hasPrefillError_dynamic` is set/cleared by `dynamicSetPrefillError`
 * (dynamic-typeahead-lookup-gate.ts, raised by dynamic-typeahead findMatchToPrefill).
 */
export function dynamicValidatorNoPrefillError(
  controlLabel: string
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const hasPrefillError =
      (control as { hasPrefillError_dynamic?: boolean } | null)
        ?.hasPrefillError_dynamic ?? false;
    // WI 35277: an auto-fill from another field failed (dynamic-typeahead-fan-out-gate.ts)
    if (
      (control as { hasFanOutError_dynamic?: boolean } | null)
        ?.hasFanOutError_dynamic
    ) {
      return {
        hasFanOutError: {
          value: `${
            controlLabel ?? 'A'
          } field could not be filled from the field it depends on. Retry before saving.`,
        },
      };
    }
    return hasPrefillError
      ? {
          hasPrefillError: {
            value: `${
              controlLabel ?? 'A'
            } field on the form could not load its saved value. Retry before saving.`,
          },
        }
      : null;
  };
}

/**
 * The validators that gate Save on a control's lookups. `registerControlForGroup` attaches
 * them to a control it creates. The prefill-error block is part of the WI 35277 opt-in
 * (`dynamicEnableLookupGate` on the root form); conflict and search-failure stay opt-in per control.
 */
export function dynamicGateValidators(
  config: IDynamicModel,
  controlLabel: string,
  group: AbstractControl
): ValidatorFn[] {
  // every control has the loading validator so a loading dropdown invalidates the form
  const validators = [dynamicValidatorNotLoading(controlLabel)];
  if (dynamicLookupGateEnabled(group) && !config.options?.lookupGateSoftFail) {
    validators.push(dynamicValidatorNoPrefillError(controlLabel));
  }
  // an unresolved conflict blocks only when the control opts into REQUIRED resolution (red icon);
  // the default (yellow) keeps the form saveable
  if (config.options?.conflictResolution?.required) {
    validators.push(dynamicValidatorNoConflict(controlLabel));
  }
  // search-failure blocking is OPT-IN ONLY (default off): blocking submit on a search outage
  // removes hazard reporting for the crews whose searches are failing
  if (config.options?.blockSubmitOnSearchFailure === true) {
    validators.push(dynamicValidatorNoSearchError(controlLabel));
  }
  return validators;
}

/**
 * WI 35277 (opt-in): a host-owned form creates its controls up front and `addControl` is a
 * no-op for an existing name, so the gate validators were never attached. When the root form
 * opted in, attach them once; a re-registration must not stack duplicates. Otherwise a no-op.
 */
export function dynamicAttachGateValidators(
  control: AbstractControl,
  validators: ValidatorFn[]
): void {
  const flagged = control as AbstractControl & {
    gateValidatorsAttached_dynamic?: boolean;
  };
  if (
    dynamicLookupGateEnabled(control) &&
    !flagged.gateValidatorsAttached_dynamic
  ) {
    flagged.gateValidatorsAttached_dynamic = true;
    control.addValidators(validators);
    control.updateValueAndValidity({ emitEvent: false });
  }
}

export function dynamicValidatorDate(
  controlLabel: string,
  type: DynamicCustomValidationTypes
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control && control.value;
    if (value) {
      // we always add one second to "today" time. This is because moment will only compare dates based on year/month/day/hours/minutes/seconds and not milliseconds!
      // therefore "today" time should always reflect 1 second ahead to ensure comparisons are "from a user experience" perspective make sense because the "today" time when the control is opened and set should always validate as behind the current "today" time always because time is always moving ahead and never standing still - let your head explode on that one.
      // Additionally the time picker only allows setting minutes and never seconds, thus the need for the handling consideration
      const today = moment().add(1, 'seconds');
      let valid = false;
      let customMessage: string;
      const controlDate = moment(value);
      switch (type) {
        case 'date: greater than current':
          if (controlDate.isAfter(today)) {
            valid = true;
          }
          break;
        case 'date: less than current':
          if (controlDate.isBefore(today)) {
            valid = true;
          }
          break;
        case 'date: greater than past 7 days':
          if (controlDate.isAfter(today.subtract(7, 'days'))) {
            valid = true;
          } else {
            customMessage = `'${controlLabel}' must be within the past 7 days in order to submit this form.`;
          }
          break;
      }
      if (!valid) {
        return {
          dateInvalid:
            customMessage ||
            `${
              controlLabel ? `'${controlLabel}'` : 'A'
            } field on the form must be ${type.replace('date: ', '')} date.`,
        };
      }
    }
    return null;
  };
}
